import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SuccessModal from "../../../components/Modals/SuccessfulModal";
import {
  createCatagory,
  updateCategoryById,
} from "../../../redux/categoriesSlice";
import { LANGUAGE_OPTIONS } from "../../constants";
import * as Yup from "yup";

const AddEditCategories = () => {
  const location = useLocation();
  const actionType = location.state?.actionType || "create";
  const categoryDetails = location.state?.categoryDetails || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModelOpen] = useState(false);
  const [imageError, setImageError] = useState("");
  const { t } = useTranslation();

  const formInitialValues =
    actionType === "edit"
      ? categoryDetails
      : {
          name: "",
          isAllowedInSubscription: false,
          languages: [{ language: "", value: "" }],
          image: "",
          isActive: false, // Default value for new category (you can change it if needed)
        };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setFileUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Category name is required")
      .min(3, "Category name should be at least 3 characters"),
    isAllowedInSubscription: Yup.boolean().required(),
    languages: Yup.array()
      .of(
        Yup.object().shape({
          language: Yup.string().required("Language is required"),
          value: Yup.string().required("Value is required"),
        })
      )
      .min(1, "At least one language is required"),
    isActive: Yup.boolean(),
    image: Yup.mixed().required("Image is required"), // Ensure image is required
  });

  return (
    <>
      <div>
        <Formik
          initialValues={formInitialValues}
          enableReinitialize={true} // Ensures the form reinitializes with new values when editing
          validationSchema={validationSchema} //
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true); // Start the submission process
            const formData = new FormData();
            // Append text fields to FormData
            formData.append("name", values.name);
            formData.append(
              "isAllowedInSubscription",
              values.isAllowedInSubscription
            );
            formData.append("languages", JSON.stringify(values.languages));
            formData.append("isActive", values.isActive);

            // Append image file to FormData
            if (file) {
              formData.append("image", file); // Image must be a File object
            }

            try {
              if (actionType === "edit") {
                // Ensure you have the correct category ID from the categoryDetails
                let updatedFormData = {
                  image: formData.get("image"),
                  isActive: formData.get("isActive"),
                  isAllowedInSubscription: formData.get(
                    "isAllowedInSubscription"
                  ),
                  languages: formData.get("languages"),
                  name: formData.get("name"),
                };

                // Assuming categoryDetails contains the _id
                const categoryId = formData._id || categoryDetails._id;

                await dispatch(
                  updateCategoryById({ categoryId, updatedFormData })
                ).then((data) => {
                  if (!data.payload.isSuccess) {
                    setErrorModelOpen(true);
                    setImageError(
                      data.payload.message || "Something Went Wrong"
                    );
                  } else {
                    setSuccessModalOpen(true);
                  }
                });
              } else {
                // Handle create category
                dispatch(createCatagory(formData)).then((data) => {
                  if (!data.payload.isSuccess) {
                    setErrorModelOpen(true);
                    setImageError(
                      data.payload.message || "Internal Server Error"
                    );
                  } else {
                    setSuccessModalOpen(true);
                  }
                });
              }
            } catch (error) {
              setImageError(error.message); // Handle the error
            } finally {
              setSubmitting(false); // Stop the submission process
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="card mt-3">
                <div className="card-body m-3">
                  <div className="row">
                    {/* Category Name */}
                    <div className="col-md-6 cs-F mb-3">
                      <TextField
                        label="Category Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        fullWidth
                      />
                    </div>

                    {/* Featured Image */}
                    <div className="row m-3">
                      <div className="banner_padding">
                        <p className="banner_heading_text">Featured Image</p>
                        <input
                          type="file"
                          onChange={(e) => {
                            setFieldValue("image", e.target.files[0]);
                            handleImageChange(e);
                          }}
                        />
                        {touched.image && errors.image && (
                          <div className="text-warning">{errors.image}</div>
                        )}
                        <img
                          src={fileUrl.length > 0 ? fileUrl : values.image}
                          accept="image/*"
                          alt=""
                          style={{
                            width: "80px",
                            height: "68px",
                            borderRadius: "12px",
                            marginLeft: "10px",
                            marginRight: "25px",
                          }}
                        />
                      </div>
                    </div>

                    {/* Languages */}
                    {values.languages?.map((l, index) => (
                      <div key={index} className="col-md-6 cs-form mb-3 mt-3">
                        <FormControl fullWidth margin="normal">
                          <Select
                            label="Language"
                            name={`languages[${index}].language`}
                            value={values.languages[index].language}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.languages &&
                              Boolean(errors.languages?.[index]?.language)
                            }
                          >
                            {LANGUAGE_OPTIONS.map((language) => (
                              <MenuItem
                                key={language.code}
                                value={language.code}
                              >
                                {language.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Value"
                          name={`languages[${index}].value`}
                          value={values.languages[index].value}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.languages &&
                            Boolean(errors.languages?.[index]?.value)
                          }
                          helperText={
                            touched.languages &&
                            errors.languages?.[index]?.value
                          }
                          fullWidth
                          margin="normal"
                        />
                        {index > 0 && (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() =>
                              setFieldValue(
                                `languages`,
                                values.languages.filter((_, i) => i !== index)
                              )
                            }
                          >
                            Remove Translation
                          </Button>
                        )}
                      </div>
                    ))}

                    {/* Add Language Button */}
                    <div className="col-md-12 cs-form mb-3 mt-3">
                      <Button
                        variant="outlined"
                        onClick={() =>
                          setFieldValue("languages", [
                            ...values.languages,
                            { language: "", value: "" },
                          ])
                        }
                      >
                        Add Language
                      </Button>
                    </div>

                    {/* Allowed in Subscription */}
                    <div className="col-md-4 cs-form mb-3 mt-3">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isAllowedInSubscription}
                            onChange={(e) =>
                              setFieldValue(
                                "isAllowedInSubscription",
                                e.target.checked
                              )
                            }
                            name="isAllowedInSubscription"
                            color="primary"
                          />
                        }
                        label="Is Allowed in Subscription?"
                      />
                    </div>

                    {/* Active Status - only in edit mode */}
                    {actionType === "edit" && (
                      <div className="col-md-4 cs-form mb-3 mt-3">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isActive}
                              onChange={(e) =>
                                setFieldValue("isActive", e.target.checked)
                              }
                              name="isActive"
                              color="primary"
                            />
                          }
                          label="Active"
                        />
                      </div>
                    )}

                    {/* Submit or Update Button */}
                    <div className="d-flex justify-content-between">
                      <Button
                        type="button"
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t("createOrder.back")}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {actionType === "edit" ? "Update" : "Submit"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {successModalOpen && (
        <SuccessModal
          isOpen={successModalOpen}
          msg={
            actionType === "edit"
              ? "Category updated successfully"
              : "Category created successfully"
          }
          navigatePage="/product-category"
        />
      )}

      {errorModalOpen && (
        <SuccessModal isOpen={errorModalOpen} msg={imageError} type="error" />
      )}
    </>
  );
};

export default AddEditCategories;
